
export const WearSlot = {
  WEAPON: 0,
  BODY: 1,
  HEAD: 2,
  SHIELD: 3,
  WRIST: 4,
  FOOT: 5,
  NECK: 6,
  EAR: 7,
  UNIQUE1: 8,
  UNIQUE2: 9,
  BELT: 10,
  COSTUME_BODY: 19,
  /** `WEAR_COSTUME_HAIR` (`length.h:126`), `WearSlot.COSTUME_HAIR` on the
   *  server. Same rule as the row above: the original's own ordinal, and not
   *  one of the eleven board cells -- only `hairCostumeVnumWorn` reads it. */
  COSTUME_HAIR: 20,
} as const;

export type WearSlotNumber = (typeof WearSlot)[keyof typeof WearSlot];

/** The eleven cells the equipment board draws, in wire order, with the
 *  abbreviations the panel puts on `data-label` and aria. The board's own
 *  twelfth face is the ARROW hole, which is not a `WearSlot` -- see
 *  `ui/bag.ts`'s `EQUIPMENT_BOARD_CELLS`. */
export const WEAR_SLOTS: readonly { readonly wear: number; readonly label: string }[] = [
  { wear: WearSlot.WEAPON, label: "Wpn" },
  { wear: WearSlot.BODY, label: "Body" },
  { wear: WearSlot.HEAD, label: "Head" },
  { wear: WearSlot.SHIELD, label: "Shld" },
  { wear: WearSlot.WRIST, label: "Wrst" },
  { wear: WearSlot.FOOT, label: "Foot" },
  { wear: WearSlot.NECK, label: "Neck" },
  { wear: WearSlot.EAR, label: "Ear" },
  { wear: WearSlot.UNIQUE1, label: "Uni1" },
  { wear: WearSlot.UNIQUE2, label: "Uni2" },
  { wear: WearSlot.BELT, label: "Belt" },
];

/** As much of a worn piece as the wardrobe needs: which slot, and which item.
 *  Structural, so both `BagSlot_` (the client's spelling) and the generated
 *  `BagSlot` (the wire's) satisfy it without either side importing the other. */
export interface WornPiece {
  readonly slot: number;
  readonly vnum: number;
}

export function wornVnum(worn: readonly WornPiece[] | undefined, slot: number): number {
  return wornIn(worn, slot)?.vnum ?? 0;
}

export function wornIn<T extends WornPiece>(
  worn: readonly T[] | undefined,
  slot: number,
): T | undefined {
  if (worn === undefined) return undefined;
  for (const piece of worn) if (piece.slot === slot) return piece;
  return undefined;
}

/** The vnum in the weapon hand. What `heldPartsFor` turns into `attach` parts. */
export function weaponVnumWorn(worn: readonly WornPiece[] | undefined): number {
  return wornVnum(worn, WearSlot.WEAPON);
}

export function bodyVnumWorn(worn: readonly WornPiece[] | undefined): number {
  const costume = wornVnum(worn, WearSlot.COSTUME_BODY);
  return costume !== 0 ? costume : wornVnum(worn, WearSlot.BODY);
}

export function hairCostumeVnumWorn(worn: readonly WornPiece[] | undefined): number {
  return wornVnum(worn, WearSlot.COSTUME_HAIR);
}
